import { useCallback } from 'react';
import { create } from 'zustand';
import { getTriggerTypes } from 'services/asset';
import { unionBy } from 'lodash';

// ======================
// Types
// ======================

export interface TriggerTypeOption {
  label: string;
  value: string;
}

interface RawTriggerType {
  id: number;
  name: string; // API returns "name", not "value"
}

interface TriggerTypeStore {
  triggerTypes: TriggerTypeOption[];
  setTriggerTypes: (types: TriggerTypeOption[]) => void;
}

// ======================
// Store
// ======================

export const useTriggerTypeStore = create<TriggerTypeStore>((set) => ({
  triggerTypes: [],
  setTriggerTypes: (triggerTypes) => set({ triggerTypes })
}));

// ======================
// Helpers
// ======================

// mirrors mapManufactureResponse in manufacture.ts
// API returns { id, name }
// label = name ("Operating Hours"), value = name ("Operating Hours")
// we use name as value because the saved schedule stores maintenance_trigger_type as a string name
const mapTriggerTypeResponse = (raw: RawTriggerType[]): TriggerTypeOption[] => {
  return raw.map((item) => ({
    label: item.name,
    value: item.name
  }));
};

// ======================
// Hooks
// ======================

/**
 * Hook to fetch trigger types — mirrors useFetchManufactures
 */
export const useFetchTriggerTypes = () => {
  const { setTriggerTypes } = useTriggerTypeStore();

  const init = useCallback(async () => {
    try {
      const data = await getTriggerTypes();
      setTriggerTypes(mapTriggerTypeResponse(data.maintenanceTriggerTypes));
    } catch (error) {
      console.error('Failed to fetch trigger types:', error);
    }
  }, [setTriggerTypes]);

  return init;
};

/**
 * Hook to force-insert a trigger type into the store during edit
 * mirrors useInsertOptionManufacturer
 */
export const useInsertOptionTriggerType = () => {
  const insert = useCallback((option: TriggerTypeOption) => {
    const { triggerTypes, setTriggerTypes } = useTriggerTypeStore.getState();
    const updated = unionBy([option], triggerTypes, 'value');
    setTriggerTypes(updated);
  }, []);

  return insert;
};
